import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    inject,
    Input,
    Modal,
    ModalHandle,
    SelectInput,
    TasksStore,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsFillCaretDownFill } from '@react-icons/all-files/bs/BsFillCaretDownFill';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { InvitesStore } from './InvitesStore';

export interface IInviteModalProps {
    readonly modalHandle: ModalHandle;
    readonly className?: string;
}

@autoinject
@observer
export class InviteModal extends React.Component<IInviteModalProps> {
    @bind
    @action
    public componentDidMount() {
        this.loadDataAsync();
        if (this.appStore.isMobile) {
            this.showForm = false;
        }
    }

    public render() {
        return (
            <Modal
                modalHandle={this.props.modalHandle}
                title="User Invite Form"
                bodyClass="minh-400"
                footer={this.footer}
            >
                {!!this.inviteUserForm?.globalErrors.length && (
                    <div className="alert-danger p-2">
                        <ul>
                            {this.inviteUserForm.globalErrors.map((e, idx) => (
                                <li className="text-danger" key={idx}>
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {this.appStore.isMobile && (
                    <Button color="link" size="sm" className="px-0" onClick={this.toggleInviteForm}>
                        {this.showForm ? 'Hide' : 'Show'} Invite Form <BsFillCaretDownFill />
                    </Button>
                )}

                {this.inviteUserForm && this.showForm && (
                    <Form className="h-100">
                        <FormGroup color={this.inviteUserForm.fields.email.state} className="container-fluid">
                            <div className="row d-flex justify-content-start">
                                <div className="col-12 mb-3">
                                    <SelectInput
                                        isMulti
                                        options={this.roleOptions}
                                        field={this.inviteUserForm.fields.roles}
                                        placeholder="Roles"
                                        required
                                    />
                                </div>

                                <div className="col-12 mb-3">
                                    <Input
                                        type="email"
                                        field={this.inviteUserForm.fields.email}
                                        placeholder="E-Mail"
                                        required
                                    />
                                </div>

                                <div className="col-12 mb-3">
                                    <SelectInput
                                        isMulti
                                        options={this.accountOptions}
                                        field={this.inviteUserForm.fields.accounts}
                                        placeholder="Accounts"
                                        required={!this.accountStore.isInRole('MriAdmin')}
                                    />
                                </div>

                                {this.accountStore.isInRoles(['MriAdmin']) && (
                                    <div className="col-12 mb-3">
                                        <Input type="checkbox" field={this.inviteUserForm.fields.subscribeToAlerts} />
                                        Subscribe User To Alerts
                                    </div>
                                )}

                                <div className="col-12 mb-3 mt-auto" />
                            </div>
                        </FormGroup>
                    </Form>
                )}
            </Modal>
        );
    }

    @bind
    @action
    private toggleInviteForm() {
        this.showForm = !this.showForm;
    }

    @bind
    @action
    private async loadDataAsync() {
        this.loading = true;

        try {
            await this.accountsStore.loadAsync();

            let accounts: string[];

            if (!this.accountStore.isInRole('MriAdmin') && !!this.accountStore.user?.accounts.length) {
                accounts = this.accountStore.user.accounts;
            } else if (this.accountStore.isInRole('MriAdmin')) {
                accounts = [];
            } else {
                throw new Error('Invalid account settings. Please contact your account administrator');
            }

            this.inviteUserForm = new FormStore<Mri.Web.Portal.Controllers.Invites.AddInviteModel>({
                accounts,
                email: '',
                roles: ['PortalUser'],
                subscribeToAlerts: true,
            });
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async submitAsync(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.inviteUserForm) {
            console.error('form does not exist!');
            return;
        }

        this.submitting = true;
        this.inviteUserForm.onSubmit();
        if (this.inviteUserForm.validate()) {
            const task = this.tasksStore.startSessionTask(`Send User Invite`);

            try {
                await this.invitesStore.inviteAsync(this.inviteUserForm.values);
                task.complete();
                this.inviteUserForm.clear();
            } catch (e: any) {
                if (e.modelState) {
                    this.inviteUserForm.clearGlobalErrors();
                    this.inviteUserForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.submitting = false;
            }
        } else {
            console.log('invalid');
            this.submitting = false;
        }
    }

    @computed
    private get accountOptions() {
        return toArray(this.accountsStore.accounts).map(a => {
            return {
                value: a.accountNumber,
                label: a.accountName,
            };
        });
    }

    @computed
    private get roleOptions() {
        if (this.accountStore.isInRole('PortalAdmin')) {
            return [
                {
                    label: 'Portal User',
                    value: 'PortalUser',
                },
                {
                    label: 'Portal Admin',
                    value: 'PortalAdmin',
                },
            ];
        } else {
            return [
                {
                    label: 'Mri Admin',
                    value: 'MriAdmin',
                },
                {
                    label: 'Mri User',
                    value: 'MriUser',
                },
                {
                    label: 'Portal Admin',
                    value: 'PortalAdmin',
                },
                {
                    label: 'Portal User',
                    value: 'PortalUser',
                },
                {
                    label: 'Production Test User',
                    value: 'ProdTestUser',
                },

                {
                    label: 'Production User',
                    value: 'ProdUser',
                },
                {
                    label: 'Portal Lite User',
                    value: 'PortalLiteUser',
                },
            ];
        }
    }

    @computed
    private get footer() {
        return (
            <button onClick={this.submitAsync} className="btn btn-primary w-100">
                {this.submitting ? 'Sending...' : 'Send Invitation Email'}
            </button>
        );
    }

    @observable
    private inviteUserForm?: FormStore<Mri.Web.Portal.Controllers.Invites.AddInviteModel>;
    @observable
    private loading = false;
    @observable
    private showForm: boolean = true;
    @observable
    private submitting: boolean = false;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private invitesStore!: InvitesStore;
    @inject
    private appStore!: AppStore;
}
